import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
} from '@mui/joy';
import { TrendingUp, ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { createApiClient } from '../../../api/poshwatch_client.ts';
import PATHS from '../../../utils/paths.js';
import { TrendAnalysis } from '../../../../libs/poshwatch_ts_client/api';
import { StyleCard } from '../../../components/StyleCard.tsx';

export const TrendingStylesPreview: React.FC = () => {
  const [trendingStyles, setTrendingStyles] = useState<TrendAnalysis[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTrendingStyles = async () => {
      try {
        setLoading(true);
        setError(null);

        const client = await createApiClient();
        const response = await client.getTrendingStyles([], 20); // Fetch first 20 results
        
        const results = response.data.results || [];
        
        // Randomly select 3 styles from the results
        const shuffled = results.sort(() => 0.5 - Math.random());
        const selected = shuffled.slice(0, 3);
        
        setTrendingStyles(selected);
      } catch (err) {
        console.error('Error fetching trending styles:', err);
        setError('Failed to load trending styles');
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingStyles();
  }, []);

  const handleViewAllTrending = () => {
    navigate(PATHS.TRENDING);
  };

  if (loading) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" />
            <Typography level="h4" sx={{ fontWeight: 'bold' }}>
              Trending Styles
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="sm"
            endDecorator={<ArrowForward />}
            onClick={handleViewAllTrending}
          >
            View All
          </Button>
        </Box>
        
        {/* Loading skeleton matching the trending page layout */}
        {[1, 2, 3].map((i) => (
          <Box
            key={i}
            sx={{
              background: '#fff',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 8,
              mb: 3,
              px: 2,
              py: { xs: 3, sm: 4 },
              boxShadow: 'none',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ width: 160, height: 160, bgcolor: 'neutral.100', borderRadius: 1, flexShrink: 0 }} />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ height: 32, bgcolor: 'neutral.100', borderRadius: 0.5, mb: 1, width: '60%' }} />
                <Box sx={{ height: 20, bgcolor: 'neutral.100', borderRadius: 0.5, mb: 1, width: '40%' }} />
                <Box sx={{ height: 20, bgcolor: 'neutral.100', borderRadius: 0.5, width: '50%' }} />
              </Box>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[1, 2, 3].map((j) => (
                <Box key={j} sx={{ flex: '1 1 220px', minWidth: 220, height: 80, bgcolor: 'neutral.100', borderRadius: 1 }} />
              ))}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (error) {
    console.error('Trending styles error:', error);
    return null; // Don't show anything if there's an error
  }

  if (trendingStyles.length === 0) {
    return null; // Don't show anything if no data
  }

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp color="primary" />
          <Typography level="h4" sx={{ fontWeight: 'bold' }}>
            Trending Styles
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="sm"
          endDecorator={<ArrowForward />}
          onClick={handleViewAllTrending}
        >
          View All
        </Button>
      </Box>

      <Box sx={{ px: 1.5, py: 1.5 }}>
        {trendingStyles.map((style, index) => {
          // Skip if no query
          if (!style.query) return null;

          return (
            <StyleCard key={`${style.query}-${index}`} style={style} index={index} />
          );
        })}
      </Box>
    </Box>
  );
};
